import { useCallback } from 'react';

import { StatusFile } from '@/hooks/useHelpfindUploady';

import {
  fetchEcommerceFormData,
  isEcommerceTransmissionPath,
} from '@/api/ecommerce';
import {
  EnumLike,
  FormData,
  OfferData,
  PaymentMethod,
  Ratings,
} from '@/api/types';
import { API_URL } from '@/consts';

import { FaqData } from './types/claimPanel';
import {
  keepPreviousData,
  UndefinedInitialDataOptions,
  useQuery,
} from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';

export const FORM_DATA_KEY_FACTORY = (id?: string) => ['form', id];

const STATIC_ASSET_EXTENSIONS =
  /\.(png|ico|jpg|jpeg|gif|svg|webp|json|css|js|woff2?|ttf|eot)$/i;
export const isValidSelfServiceId = (id: string | undefined): boolean =>
  !!id && !STATIC_ASSET_EXTENSIONS.test(id);

export const fetchFormData = async (
  id: string,
  isEcommerce = false
): Promise<FormData> => {
  if (isEcommerce) return fetchEcommerceFormData(id);

  const searchParams = new URLSearchParams(window.location.search);
  const hash = searchParams.get('hash');
  const hashUrl = hash ? `?hash=${hash}` : '';
  const data = await fetch(`${API_URL}/api/self-service/${id}${hashUrl}`, {
    headers: {
      accept: 'application/json',
    },
  });

  return data.json();
};

export const formDataQueryOptions = (
  id: string | undefined,
  isEcommerce = false
) =>
  ({
    queryKey: FORM_DATA_KEY_FACTORY(id),
    queryFn: ({ queryKey }) =>
      fetchFormData(queryKey[1] as string, isEcommerce),
    retry: false,
    staleTime: Infinity,
    enabled: isValidSelfServiceId(id),
    throwOnError: true,
    placeholderData: keepPreviousData,
  }) as UndefinedInitialDataOptions<
    FormData,
    Error,
    FormData,
    (string | undefined)[]
  >;
export const useFormDataQuery = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  return useQuery(
    formDataQueryOptions(id, isEcommerceTransmissionPath(pathname))
  );
};

export const useRatingQuery = () => {
  return useQuery({
    queryKey: ['ratings'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/ratings`).then((res) =>
        res.json()
      ) as Promise<Ratings>,
    retry: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours,
  });
};

const fetchPresent = async (id: string) => {
  const url = new URL(`${API_URL}/api/self-service/${id}/present`);
  const data = await fetch(url, {
    headers: {
      accept: 'application/json',
    },
  });
  return data.json();
};

export const fetchLeave = async (id: string) => {
  const url = new URL(`${API_URL}/api/self-service/${id}/leave`);
  const data = await fetch(url, {
    headers: {
      accept: 'application/json',
    },
  });
  return data.json();
};

export const usePresentQuery = (id: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['present', id],
    queryFn: () => fetchPresent(id),
    refetchInterval: 15000,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
export type Attachment = {
  id: number;
  name: string;
  password: string | null;
  size: number;
  download_url: string;
  can_be_managed: boolean;
  status?: StatusFile;
};
const fetchAttachments = async (
  id: string,
  attachment_type_id?: number | string
): Promise<Attachment[]> => {
  const url = new URL(`${API_URL}/api/self-service/${id}/attachments`);
  if (attachment_type_id)
    url.searchParams.set('attachment_type_id', attachment_type_id.toString());

  const data = await fetch(url, {
    headers: {
      accept: 'application/json',
    },
  });
  return data.json();
};

export const useAttachmentQuery = (attachment_type_id?: string | number) => {
  const { id } = useParams();
  if (!id) throw new Error('Hash is not provided');
  return useQuery({
    queryKey: ['attachments', id, attachment_type_id],
    queryFn: () => fetchAttachments(id, attachment_type_id),
    retry: false,
  });
};

export const useInsurancesCompaniesQuery = () => {
  return useQuery({
    queryKey: ['insurance-companies'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/insurance-companies`).then((res) =>
        res.json()
      ) as Promise<EnumLike[]>,
    retry: false,
    select: useCallback(
      (data: EnumLike[]) =>
        data
          .map(({ value, label }: EnumLike) => ({
            value: value.toString(),
            label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
          .filter(({ label }) => label !== 'Inne'),
      []
    ),
    staleTime: 1000 * 60 * 30, // 30 min,
  });
};

export const useBanksQuery = () => {
  return useQuery({
    queryKey: ['banks'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/banks`).then((res) =>
        res.json()
      ) as Promise<EnumLike[]>,
    retry: false,
    select: useCallback(
      (data: EnumLike[]) =>
        data
          .map(({ value, label }: EnumLike) => ({
            value: value.toString(),
            label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
          .filter(({ label }) => label !== 'Inne'),
      []
    ),
    staleTime: 1000 * 60 * 30, // 30 min,
  });
};

export const usePaymentMethodsQuery = () => {
  return useQuery({
    queryKey: ['payment-methods'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/e-commerce/payment-methods`, {
        headers: { accept: 'application/json' },
      }).then((res) => res.json()) as Promise<PaymentMethod[]>,
    retry: false,
    staleTime: 1000 * 60 * 30, // 30 min,
  });
};

export const useIssueTypeQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['issue-types'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/issue-types`).then((res) =>
        res.json()
      ) as Promise<EnumLike[]>,
    retry: false,
    select: useCallback(
      (data: EnumLike[]) =>
        data
          .map(({ value, label }: EnumLike) => ({
            value: value.toString(),
            label,
          }))
          .sort((a, b) => {
            if (a.label === 'Inne') return 1;
            if (b.label === 'Inne') return -1;
            return a.label.localeCompare(b.label);
          }),
      []
    ),
    enabled,
    staleTime: 1000 * 60 * 30, // 30 min,
  });
};

export const useTaxOfficesQuery = () => {
  return useQuery({
    queryKey: ['tax-offices'],
    queryFn: () =>
      fetch(`${API_URL}/api/self-service/taxes`).then((res) => {
        if (res.status === 429) {
          throw new Error(
            'Jesteś za szybki dla serwera, spróbuj ponownie później'
          );
        }
        return res.json();
      }) as Promise<EnumLike[]>,
    retry: false,
    select: useCallback(
      (data: EnumLike[]) =>
        data
          .map(({ value, label }: EnumLike) => ({
            value: value.toString(),
            label,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
          .filter(({ label }) => label !== 'Inne'),
      []
    ),
    staleTime: 1000 * 60 * 30, // 30 min,
    throwOnError: true,
  });
};

export const getCities = async (
  query: string,
  postalCode: string,
  city?: string | number
): Promise<EnumLike[]> => {
  if (!query && city === undefined) return [];
  const selection =
    city === undefined
      ? `query=${encodeURIComponent(query)}`
      : `city=${encodeURIComponent(city)}`;
  const response = await fetch(
    `${API_URL}/api/self-service/city?postal=${postalCode.replace('-', '')}&${selection}`
  );
  if (!response.ok) throw new Error('Unable to load cities');
  return response.json();
};

export const useCitiesQuery = (
  query: string,
  postalCode: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ['cities', postalCode, query],
    queryFn: () => getCities(query, postalCode),
    enabled,
  });
};

const getStreets = async (query: string, city: string): Promise<EnumLike[]> => {
  if (!query) return [];
  const response = await fetch(
    `${API_URL}/api/self-service/street?city=${city}&query=${encodeURIComponent(query)}`
  );
  return response.json();
};

export const useStreetsQuery = (
  query: string,
  city: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ['streets', city, query],
    queryFn: () => getStreets(query, city),
    enabled,
  });
};

const getOffers = async (hash: string): Promise<OfferData> => {
  const response = await fetch(
    `${API_URL}/api/self-service/${hash}/fetch-offer`,
    {
      headers: {
        accept: 'application/json',
      },
    }
  );
  return response.json();
};

export const useOffers = () => {
  const { id } = useParams();
  if (!id) throw new Error('Hash is not provided');
  return useQuery({
    queryKey: ['offers', id],
    queryFn: () => getOffers(id),
  });
};

const getFaqSkdClaimAssignment = async (): Promise<FaqData> => {
  const response = await fetch(`${API_URL}/api/self-service/faq`);
  return response.json();
};

export const useFaqSkdClaimAssignmentQuery = () => {
  return useQuery({
    queryKey: ['faq-skd-claim-assignment'],
    queryFn: getFaqSkdClaimAssignment,
  });
};
